import React from 'react'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'

import {BaseModal} from '../../components/BaseModal'
import {useThemeStore} from '../../stores/themeStore'
import {isSafeLogoUrl} from '../../utils/companyLogo'

// The same Cloudinary "Powered by TrueDesking" asset the copy/paste card and PDF
// footer use, so the previews match real output branding.
const TRUEDESKING_LOGO = 'https://res.cloudinary.com/hgjsxpz9b/image/upload/v1770308142/td-logo_ule6rf.png'

// Read-only illustration of how the account's logo appears at the bottom of a
// Copy Paste quote card and a Customer Worksheet. These are lightweight static
// approximations with representative sample data — deliberately NOT the live
// CopyQuoteTooltip / react-pdf renderers, which require a full dealState. They
// only ever show the current logo (or the company-name fallback) plus the
// unchanged "Powered by" attribution.
export const LogoPreviewModal = ({logoUrl, companyName, onClose}) => {
    const theme = useThemeStore((state) => state.theme)
    const hasLogo = isSafeLogoUrl(logoUrl)

    const Branding = ({className}) =>
        hasLogo
            ? <img className={className} src={logoUrl} alt={`${companyName || 'Company'} logo`}/>
            : <span className={'preview-logo-text'}>{companyName || 'Your Company'}</span>

    const PoweredBy = () => (
        <span className={'preview-powered'}>
            Powered by: <img src={TRUEDESKING_LOGO} alt={'TrueDesking'}/>
        </span>
    )

    const wrapperStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'transparent',
        boxShadow: 'none',
        outline: 'none',
        padding: 0,
        width: 'min(94vw, 1000px)',
    }

    return (
        <BaseModal isOpen setIsOpen={() => onClose()} style={wrapperStyle}>
            <div className={'LogoPreviewModal'} data-theme={theme} role={'dialog'} aria-label={'Logo preview'}>
                <div className={'LogoPreviewModal__header'}>
                    <span className={'header-title'}>
                        <VisibilityOutlinedIcon/>
                        Logo Preview
                    </span>
                    <button
                        type={'button'}
                        className={'LogoPreviewModal__close'}
                        aria-label={'Close preview'}
                        onClick={() => onClose()}
                    >
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="6" y1="6" x2="18" y2="18"/>
                            <line x1="18" y1="6" x2="6" y2="18"/>
                        </svg>
                    </button>
                </div>

                <p className={'LogoPreviewModal__desc'}>
                    This is how your logo will appear at the bottom of worksheets and Copy Paste quote cards.
                </p>

                <div className={'LogoPreviewModal__body'}>
                    {/* Copy Paste preview */}
                    <section className={'LogoPreviewModal__col'}>
                        <span className={'col-label'}>Copy Paste Preview</span>
                        <span className={'col-sub'}>Example of your logo at the bottom of a Copy Paste quote card.</span>

                        <div className={'preview-card preview-copy'}>
                            <div className={'pc-title'}>
                                <strong>2026 Acura Integra</strong><br/>
                                4D SEDAN
                            </div>
                            <div className={'pc-row'}><span>MSRP:</span><span className={'pc-val'}>$30,000.00</span></div>
                            <div className={'pc-row'}><span>Total Drive Off:</span><span className={'pc-val'}>$777.11</span></div>
                            <div className={'pc-row'}><span>Payment:</span><span className={'pc-val'}>$533.81</span></div>
                            <div className={'pc-row'}><span>Term:</span><span className={'pc-val'}>39 months</span></div>
                            <div className={'pc-footer'}>
                                <Branding className={'preview-logo'}/>
                                <PoweredBy/>
                            </div>
                        </div>
                    </section>

                    {/* Worksheet preview */}
                    <section className={'LogoPreviewModal__col'}>
                        <span className={'col-label'}>Worksheet Preview</span>
                        <span className={'col-sub'}>Example of your logo at the bottom of a Customer Worksheet.</span>

                        <div className={'preview-card preview-worksheet'}>
                            <div className={'pw-head'}>
                                <span>Customer Worksheet</span>
                                <span>07/24/2026</span>
                            </div>
                            <div className={'pw-grid'}>
                                <div className={'pw-box'}>
                                    <div className={'pw-row'}><span>Amount Due at Signing</span></div>
                                    <div className={'pw-row'}><span>First Monthly Payment</span><span>$533.81</span></div>
                                    <div className={'pw-row'}><span>Down Pmt. Tax @ 9.75%</span><span>$97.50</span></div>
                                </div>
                                <div className={'pw-box'}>
                                    <div className={'pw-row'}><span>Tax Rate</span><span>9.75%</span></div>
                                    <div className={'pw-row'}><span>Miles Per Year</span><span>10000</span></div>
                                </div>
                            </div>
                            <div className={'pw-footer'}>
                                <Branding className={'preview-logo'}/>
                                <PoweredBy/>
                            </div>
                        </div>
                    </section>
                </div>

                <div className={'LogoPreviewModal__footer'}>
                    <span className={'footer-note'}>Note: Previews are for reference only. Actual output may vary slightly.</span>
                    <button type={'button'} className={'LogoPreviewModal__close-btn'} onClick={() => onClose()}>
                        Close
                    </button>
                </div>
            </div>
        </BaseModal>
    )
}
